import {CardElement, Elements, useElements, useStripe} from "@stripe/react-stripe-js";
import React, {useEffect, useState} from "react";
import InvisiblePane from "../../../../Components/InvisiblePane";
import Text from "../../../../Components/Text";
import Pane from "../../../../Components/Pane";
import ProgressBar from "../../../../Components/ProgressBar";
import BackButton from "../../../../Components/BackButton";
import Button from "../../../../Components/Button";
import PrimaryTitle from "../../../../Components/PrimaryTitle";
import FormInput from "../../../../Components/Forms/FormInput";
import {cloneDeep} from "lodash";
import Label from "../../../../Components/Forms/Label";
import Error from "../../../../Components/Forms/Error";
import {DEFAULT_ERROR_MESSAGE, JOURNEYS, prependJourneyToRouteName} from "../../../../constants";
import PageLoadingPanel from "../../../../Components/PageLoadingPanel";
import SubscriptionDetails from "./SubscriptionDetails";
import VoucherDetails from "./VoucherDetails";
import SecondaryTitle from "../../../../Components/SecondaryTitle";
import AgentPanel from "../../../../Components/AgentPanel";
import Loader from "../../../../Components/Loader";
import {default as ErrorMessage} from "../../../../Components/Error";

const StripeChargeForm = (props) => {
    const {
        journey,
        previousPage    = () => {},
        nextPage        = () => {},
    } = props;

    const stripe    = useStripe();
    const elements  = useElements();

    const PAGE_TITLE            = 'Subscription';
    const CURRENT_STEP_NUMBER   = 3;
    const TOTAL_STEP_NUMBER     = 4;

    const [data, setData]                   = useState({
        account_holder_name:    '',
        subscription:           null,
        setup_intent:           null
    });
    const [voucher, setVoucher]                         = useState(null);
    const [loaded, setLoaded]                           = useState(false);
    const [errors, setErrors]                           = useState(null);
    const [errorMessage, setErrorMessage]               = useState(null);
    const [submitting, setSubmitting]                   = useState(false);
    const [subscriptionFailure, setSubscriptionFailure] = useState(false);

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        if (submitting) {
            submit();
        }
    }, [submitting]);

    const load = () => {
        axios.get(route(prependJourneyToRouteName(journey, 'session.stripe-subscription.get'), {session: window.session}).toString())
            .then((response) => {
                if (!!response && !!response.data) {
                    setData(response.data);
                }
            })
            .catch((error) => {
                console.log(error);

                // Check for the status returned if a company has already been subscribed.
                if (error.response.status === 422) {
                    setSubscriptionFailure(true);
                }
            }).finally(() => {
                setLoaded(true);
            });

    }

    const validate = () => {
        const errors = {};

        if (!data.account_holder_name || data.account_holder_name === '') {
            errors.account_holder_name = 'The account holder name field is required';
        }

        return errors;
    }

    const submit = async () => {
        setErrors(null);
        setErrorMessage(null);

        if (!stripe || !elements || !data || !data.setup_intent) {
            // Stripe.js has not yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }

        let validation = validate();

        const cardElement = elements.getElement(CardElement);

        stripe.confirmCardSetup(data.setup_intent, {
            payment_method: {
                card:   cardElement,
                billing_details: {
                    name: data.account_holder_name
                },
            }
        }).then((response) => {
            if (!!response && !!response.error && !!response.error.message) {
                handleStripeErrors(validation, response.error.message);
                setSubmitting(false);
                setErrorMessage('Please check Stripe details.');
            } else {
                updateApi(response.setupIntent.payment_method);
            }
        }).catch((error) => {
            console.log(error);
            if (!!error && !!error.error && !!error.error.message) {
                handleStripeErrors(validation, error.error.message);
            }

            setSubmitting(false);
            setErrorMessage('Please check Stripe details.');
        });
    }

    const updateApi = (paymentMethodId) => {
        axios.patch(route(prependJourneyToRouteName(journey, 'session.stripe-subscription.update'), {session: window.session, journey: journey}).toString(), {
            account_holder_name:    data.account_holder_name,
            payment_method_id:      paymentMethodId,
            voucher_code:           !!voucher && !!voucher.code ? voucher.code : null
        })
            .then((response) => {
                if (!!response && !!response.data) {
                    if (!!response.data.success && response.data.success) {
                        setSubmitting(false);
                        nextPage();
                    }
                }
            })
            .catch((error) => {
                if (!!error && !!error.response && !!error.response.data) {
                    if (!!error.response.data.message) {
                        setErrorMessage(error.response.data.message);
                    }

                    if (!!error.response.data.errors) {
                        setErrors(error.response.data.errors);
                    }

                    if (!!error.response.data.redirect) {
                        window.location.href = error.response.data.redirect;
                    }
                } else {
                    console.log(error);
                    setErrorMessage(DEFAULT_ERROR_MESSAGE);
                }
            }).finally(() => {
                setSubmitting(false);
            });
    }

    const handleStripeErrors = (validation, message) => {
        validation.stripe_card_element = message;

        setErrors(validation);
    }

    const handleUpdate = (field, value) => {
        let clone = cloneDeep(data);

        switch (field) {
            default:
                clone[field] = value;
                break;
        }

        setData(clone);
    }

    if (!!subscriptionFailure) {
        return (
            <React.Fragment>
                <ErrorMessage message={'The process is no longer completable via this journey.'} />
                <Pane>
                    {journey !== JOURNEYS.PAYMENT && (
                        <ProgressBar
                            title={PAGE_TITLE}
                            currentStepNumber={CURRENT_STEP_NUMBER}
                            totalStepNumber={TOTAL_STEP_NUMBER}
                        />
                    )}
                    <PrimaryTitle>{PAGE_TITLE}</PrimaryTitle>
                    <Text>SB Pro has found that the subscription has already been processed. Please contact SB Pro support.</Text>
                </Pane>
                {journey !== JOURNEYS.PAYMENT && (
                    <AgentPanel journey={journey} />
                )}
            </React.Fragment>
        );
    }

    if (!loaded && journey !== JOURNEYS.PAYMENT) {
        return (
            <PageLoadingPanel
                pageTitle={PAGE_TITLE}
                currentStepNumber={CURRENT_STEP_NUMBER}
                totalStepNumber={TOTAL_STEP_NUMBER}
                upperTextComponent={null}
                journey={journey}
            />
        )
    } else if(!loaded && journey === JOURNEYS.PAYMENT) {
        return (
            <Pane>
                <PrimaryTitle>{PAGE_TITLE}</PrimaryTitle>
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    <Loader />
                </div>
            </Pane>
        )
    }

    return (
        <React.Fragment>
            {!!errorMessage && (
                <ErrorMessage message={errorMessage} />
            )}
            <Pane>
                {journey !== JOURNEYS.PAYMENT && (
                    <ProgressBar
                        title={PAGE_TITLE}
                        currentStepNumber={CURRENT_STEP_NUMBER}
                        totalStepNumber={TOTAL_STEP_NUMBER}
                    />
                )}
                <PrimaryTitle>Subscription</PrimaryTitle>
                <SubscriptionDetails subscription={data.subscription} />
                <VoucherDetails
                    appliedVoucher={voucher}
                    setAppliedVoucher={(value) => setVoucher(value)}
                    disabled={submitting}
                    journey={journey}
                />
                <SecondaryTitle>Payment details</SecondaryTitle>
                <FormInput
                    name={'account_holder_name'}
                    label={'Account holder name'}
                    value={data.account_holder_name}
                    type={'text'}
                    onChange={(value) => handleUpdate('account_holder_name', value)}
                    disabled={submitting}
                    error={errors}
                />
                <div className={"form-input-container"}>
                    <div className="form-input">
                        <Label name={'stripe_card_element'} label={'Card details'} />
                        <div style={{paddingTop: '7px', paddingBottom: '7px'}}>
                            <CardElement
                                options={{
                                    disabled: submitting,
                                }}
                            />
                        </div>
                    </div>
                    <Error error={!!errors && !!errors['stripe_card_element'] ? errors['stripe_card_element'] : null} />
                </div>
            </Pane>
            {!!errorMessage && (
                <ErrorMessage message={errorMessage} />
            )}
            <InvisiblePane style={{paddingTop: '30px', paddingLeft: 0, paddingRight: 0, display: 'flex', justifyContent: !!previousPage ? 'space-between' : 'center', alignItems: 'center'}}>
                {!!previousPage && (
                    <BackButton
                        disabled={submitting}
                        onClick={() => previousPage()}
                    />
                )}
                <Button
                    label={submitting ? 'Submitting...' : 'Next'}
                    disabled={submitting}
                    onClick={() => setSubmitting(true)}
                />
            </InvisiblePane>
            {journey !== JOURNEYS.PAYMENT && (
                <AgentPanel journey={journey} />
            )}
        </React.Fragment>
    );
}

export default StripeChargeForm;
